// 📄 B250 (AI_113) — MONTADOR DO CONTEXTO CLÍNICO DO LAUDO ESPECIAL
// Sistema: HEPATO TX RONDÔNIA
// ─────────────────────────────────────────────────────────────
// Função PURA que formata os dados do paciente num bloco markdown para a etapa
// `laudo-especial` do /api/analyze. Espelha a formatação de DADOS do chat de
// discussão (gerarPromptSistema), mas SEM as regras clínicas — o piso de
// segurança e o papel ("você é apoio", ancoragem B224) vivem no SYSTEM PROMPT
// do backend (fonte única). Aqui só montamos o que a IA precisa LER.
//
// Privacidade: `dados` vem de coletarDadosCompletos (já pseudonimizado). O NOME
// do paciente entra no cabeçalho do PDF (no front), não neste bloco — mesma
// postura do chat. Pura → testável (sem React/Firestore).
// ─────────────────────────────────────────────────────────────

import { valoresLabParaIA } from "./valoresLabParaIA";
/**
 * Monta o bloco clínico (markdown) que alimenta a etapa `laudo-especial`.
 * @param dados saída de coletarDadosCompletos (pseudonimizada)
 */
export function montarContextoLaudoEspecial(dados: any): string {
  const linhas: string[] = [];

  // Rótulo NEUTRO de propósito: o piso do system prompt proíbe escrever
  // "(pseudonimizados)" no laudo; não apresentar o termo nem no input.
  linhas.push("## DADOS DO PACIENTE:");
  linhas.push(`- **Idade:** ${dados?.idade ?? "não informado"} anos`);
  linhas.push(`- **Sexo:** ${dados?.sexo ?? "não informado"}`);
  if (dados?.tipoAtendimento)
    linhas.push(`- **Tipo de atendimento:** ${dados.tipoAtendimento}`);
  if (dados?.diagnosticoBase)
    linhas.push(`- **Diagnóstico de base:** ${dados.diagnosticoBase}`);

  // Transplante (quando houver)
  if (dados?.transplante) {
    linhas.push("");
    linhas.push("## DADOS DO TRANSPLANTE:");
    if (dados.transplante.dataTransplante)
      linhas.push(`- Data: ${dados.transplante.dataTransplante}`);
    if (dados.transplante.tempoTransplante)
      linhas.push(`- Tempo pós-Tx: ${dados.transplante.tempoTransplante}`);
    if (dados.transplante.indicacao)
      linhas.push(`- Indicação: ${dados.transplante.indicacao}`);
  }

  // Consultas (10 mais recentes)
  linhas.push("");
  linhas.push(
    "## CONSULTAS RECENTES (até 10 mais recentes, ordem cronológica decrescente):"
  );
  const consultas = Array.isArray(dados?.consultas) ? dados.consultas : [];
  if (consultas.length === 0) {
    linhas.push("Nenhuma consulta registrada");
  } else {
    consultas.slice(0, 10).forEach((c: any) => {
      const bloco: string[] = [`### ${c.data || "Data não registrada"}`];
      if (c.queixaPrincipal)
        bloco.push(`**Queixa principal:** ${c.queixaPrincipal}`);
      if (c.hma) bloco.push(`**História da doença atual:** ${c.hma}`);
      if (c.exameFisico) bloco.push(`**Exame físico:** ${c.exameFisico}`);
      if (c.hipoteses) bloco.push(`**Hipóteses diagnósticas:** ${c.hipoteses}`);
      if (c.conduta) bloco.push(`**Conduta:** ${c.conduta}`);
      if (c.observacoes) bloco.push(`**Observações:** ${c.observacoes}`);
      linhas.push(bloco.join("\n"));
      linhas.push("");
    });
  }

  // Exames laboratoriais (3 mais recentes). ⚠️ ATENÇÃO À ORDEM (diferente dos
  // marcadores): embora o Firestore traga desc, o anonimizarDados RE-ORDENA os
  // labs em ASCENDENTE (mais antigo→recente, p/ a IA ler a tendência cronológica
  // — anonimizarDados.ts ~189) → os mais recentes ficam no FIM → slice(-3).
  linhas.push("## EXAMES LABORATORIAIS RECENTES:");
  const labs = Array.isArray(dados?.examesLab) ? dados.examesLab : [];
  if (labs.length === 0) {
    linhas.push("Nenhum exame laboratorial disponível");
  } else {
    labs.slice(-3).forEach((e: any) => {
      linhas.push(
        `- **${e.data || "data não informada"}:** ${
          e.valores ? valoresLabParaIA(e.valores) : "—"
        }`
      );
    });
  }

  // Marcadores tumorais (3 mais recentes). Idem: array em ordem DECRESCENTE por
  // data (orderBy "dataColeta","desc") → mais recentes no INÍCIO → slice(0, 3).
  linhas.push("");
  linhas.push("## MARCADORES TUMORAIS RECENTES:");
  const marcadores = Array.isArray(dados?.marcadores) ? dados.marcadores : [];
  if (marcadores.length === 0) {
    linhas.push("Nenhum marcador disponível");
  } else {
    marcadores.slice(0, 3).forEach((m: any) => {
      const data = m.data || m.dataColeta || "data não informada";
      const afp = m.afp ?? m.AFP ?? "N/A";
      linhas.push(`- **${data}:** AFP ${afp} ng/mL`);
    });
  }

  // Exames de imagem
  linhas.push("");
  const imagens = Array.isArray(dados?.imagens) ? dados.imagens : [];
  linhas.push(`## EXAMES DE IMAGEM REALIZADOS (${imagens.length}):`);
  if (imagens.length === 0) {
    linhas.push("Nenhum exame de imagem registrado");
  } else {
    imagens.forEach((img: any) => {
      const data = img.dataExame || img.data || "data não informada";
      const bloco: string[] = [`### ${img.tipo || "Exame"} — ${data}`];
      if (img.descricao) bloco.push(`Descrição: ${img.descricao}`);
      if (img.achados) {
        bloco.push(
          `- Lesões: ${img.achados.lesoesHepaticas || "Não descrito"}`
        );
        bloco.push(`- Status Milão: ${img.achados.statusMilao || "N/A"}`);
        bloco.push(`- Trombose portal: ${img.achados.trombosePortal || "N/A"}`);
        if (img.achados.conclusao)
          bloco.push(`- Conclusão: ${img.achados.conclusao}`);
      } else {
        bloco.push("- Exame ainda não analisado pela IA");
      }
      linhas.push(bloco.join("\n"));
      linhas.push("");
    });
  }

  return linhas.join("\n").trim();
}
